import React, {FormEvent, ReactNode} from "react";
import {CheckboxItem, styled} from "@reside/ui";
import {UserPermissionEnum} from "../../../../utils/types/user";
import {without} from "lodash";

import {
  TemporaryHideChart,
  Props as TemporaryHideChartProps,
} from "../../TemporaryHideChart";
import {PERMISSION_BLOCKS} from "../../permissions.helpers";
import {PermissionPendingSuffix} from "./PermissionPendingSuffix";
import {
  PermissionCategoryCard,
  PermissionCategoryItem,
} from "./PermissionCategoryCard";

type Props = Readonly<{
  label: ReactNode;
  permissions: ReadonlyArray<{
    value: UserPermissionEnum;
    label: string;
  }>;
  activePermissions?: ReadonlyArray<UserPermissionEnum>;
  isPermissionPending?: (permission: UserPermissionEnum) => boolean;
  onSetPermission: (permission: ReadonlyArray<UserPermissionEnum>) => void;
  TemporaryHideChart: (props: TemporaryHideChartProps) => JSX.Element;
}>;

export const PermissionsMacroCheckbox = ({
  label,
  permissions,
  activePermissions = [],
  isPermissionPending,
  onSetPermission,
  TemporaryHideChart,
}: Props) => {
  const permissionValues = permissions.map(({value}) => value);

  const isActive = (value: UserPermissionEnum) =>
    activePermissions.includes(value);

  const everyChecked = permissions.every(({value}) => isActive(value));
  const someChecked = permissions.some(({value}) => isActive(value));
  const selectedCount = permissions.filter(({value}) =>
    isActive(value),
  ).length;
  const categoryPending = permissionValues.some(
    value => isPermissionPending?.(value) ?? false,
  );

  const setValues = (values: ReadonlyArray<UserPermissionEnum>) => {
    onSetPermission([
      ...values,
      ...without(activePermissions, ...permissionValues),
    ]);
  };

  const onGroupChange = (event: FormEvent<HTMLInputElement>) =>
    setValues(
      event.currentTarget.checked ? permissionValues : [],
    );

  const onOneChange =
    (value: UserPermissionEnum) => (event: FormEvent<HTMLInputElement>) => {
      const groupValues = activePermissions.filter(permission =>
        permissionValues.includes(permission),
      );

      setValues(
        event.currentTarget.checked
          ? [...groupValues, value]
          : groupValues.filter(permission => permission !== value),
      );
    };

  return (
    <PermissionCategoryCard
      label={label}
      selectedCount={selectedCount}
      totalCount={permissions.length}
      someChecked={someChecked}
      everyChecked={everyChecked}
      isPending={categoryPending}
      onGroupChange={onGroupChange}
    >
      {permissions.map(({value, label: itemLabel}) => (
        <PermissionCategoryItem key={value}>
          <TemporaryHideChart chartIds={PERMISSION_BLOCKS[value] || []}>
            <PermissionRow>
              <CheckboxItem
                label={itemLabel}
                checked={everyChecked || isActive(value)}
                onChange={onOneChange(value)}
              />
              {isPermissionPending?.(value) ? <PermissionPendingSuffix /> : null}
            </PermissionRow>
          </TemporaryHideChart>
        </PermissionCategoryItem>
      ))}
    </PermissionCategoryCard>
  );
};

const PermissionRow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  width: 100%;
`;

PermissionsMacroCheckbox.defaultProps = {
  TemporaryHideChart,
};
